import {
	Box,
	Grid,
	Card,
	CardContent,
	Typography,
	Alert,
	Snackbar,
} from '@mui/material';
import {
	FileText,
	CheckCircle,
} from 'lucide-react';
import React, { useEffect, useState, useRef } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { ApproveClient, GetBusinessInformation, GetClientDocuments, UpdateDocuments } from "../../../services/client.services";
import { useParams } from "react-router-dom";
import { LoadingBox } from "../../../components/Box";
import { ContainedSearchButton, OutlinedButton } from '../../../components/Buttons';
import DownloadIcon from '@mui/icons-material/Download';

export default function ClientContract() {
	const { id } = useParams();
	const [clientDocuments, setClientDocuments] = useState(null);
	const [businessId, setBusinessId] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const hasRun = useRef(false);
	const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

	const fetchData = async () => {
		setIsLoading(false);
		try {
			setIsLoading(true);

			const businessInfo = await GetBusinessInformation(id);
			setBusinessId(businessInfo.id); // store in state (async)

			const clientDocs = await GetClientDocuments(businessInfo.id, 'BUSINESS_AGREEMENT');

			if (clientDocs.status === 404) {
				setClientDocuments(null);
			} else {
				setClientDocuments(URL.createObjectURL(clientDocs));
			}

			setIsLoading(false);
		} catch (error) {
			throw error;
		}
	};

	useEffect(() => {
		if (hasRun.current) return;
		hasRun.current = true;

		fetchData();
		// eslint-disable-next-line
	}, [id]);

	if (isLoading) return (
		<LoadingBox>
			<CircularProgress />
		</LoadingBox>
	);

	const handleReviewSubmit = async (status, note) => {
		try {
			setIsLoading(true);
			const payload = {
				status,
				note
			}
			await UpdateDocuments(payload, businessId, 'BUSINESS_AGREEMENT').then((data) => {
				setSnackbar({
					open: true,
					message: `Document ${status === 'APPROVED' ? 'approved' : 'rejected'} successfully`,
					severity: 'success'
				});
				fetchData();
			});
		} catch (error) {
			setSnackbar({
				open: true,
				message: 'Failed to update document status',
				severity: 'error'
			});
		} finally {
			setIsLoading(false);
		}
	};

	const handleApproveClient = async () => {
		try {
			setIsLoading(true);
			await ApproveClient(businessId);
			setSnackbar({ open: true, message: "Client information updated successfully!", severity: 'success' });
		} catch (error) {
			console.error("Failed to update client", error);
			setSnackbar({ open: true, message: "Update failed. Try again later.", severity: 'error' });
		} finally {
			setIsLoading(false);
		}
	}

	const hasPendingOrRejected = () => {
		return !clientDocuments;
		// return clientDocumentsTypes.some(doc =>
		// 	["PENDING", "REJECTED"].includes(doc.documentApprovalStatus)
		// );
	}
	return (
		<Box>

			<Grid container spacing={3}>
				<Grid item xs={12}>
					<Card>
						<CardContent>
							<Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
								<FileText size={24} style={{ marginRight: 12 }} />
								<Box sx={{ flex: 1 }}>
									{clientDocuments ? (
										<>
											<Box sx={{ mb: 2 }}>
												<iframe
													src={clientDocuments}
													title="PDF Viewer"
													width="100%"
													height="600px"
													style={{ border: 'none' }}
												/>
											</Box>

											<Box sx={{ display: 'flex', gap: 1 }}>
												<OutlinedButton
													variant="outlined"
													size="small"
													startIcon={<DownloadIcon size={16} />}
													onClick={() => {
														const link = document.createElement("a");
														link.href = clientDocuments;
														link.download = 'BUSINESS_AGREEMENT';
														link.click();
														URL.revokeObjectURL(clientDocuments);
													}}
												>
													Download
												</OutlinedButton>
												<ContainedSearchButton
													variant="contained"
													size="small"
													startIcon={<CheckCircle size={16} />}
													onClick={() => handleReviewSubmit('APPROVED', '')}
												>
													Approve
												</ContainedSearchButton>
											</Box>
										</>
									) : (
										<Typography color="text.secondary">
											No contract found
										</Typography>
									)}
								</Box>
							</Box>
						</CardContent>
					</Card>
				</Grid>

			</Grid>
			<br></br>
			{hasPendingOrRejected() ?
				<ContainedSearchButton variant="contained" disabled>
					Approve Client
				</ContainedSearchButton>
				:
				<ContainedSearchButton variant="contained" onClick={handleApproveClient}>
					Approve Clients
				</ContainedSearchButton>
			}
			<Snackbar
				open={snackbar.open}
				autoHideDuration={6000}
				onClose={() => setSnackbar({ ...snackbar, open: false })}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert
					onClose={() => setSnackbar({ ...snackbar, open: false })}
					severity={snackbar.severity}
				>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</Box>
	);
}
